import { getDevice } from 'framework7'
import { DateTime } from 'luxon'
import { ofetch } from 'ofetch'

import Reservation from '@/assets/classes/parking/Reservation'
import Vehicle from '@/assets/classes/parking/Vehicle'
import { checkUserPermissions, getAuthorizationToken } from '@/assets/composables/auth'
import { ToReservation } from '@/assets/composables/mappers'
import {
  devParkingReservations,
  getDevParkingReservations,
  getDevCardLoans,
  devCardLoanId,
  configureDevParkingReturnHours,
  cancelDevParkingReservation,
} from '@/assets/composables/parking/devReservations'
import { supabase } from '@/assets/composables/supabase'
import {
  AvailableSlots,
  CardLoanList,
  BanResponse,
  DefaultSettings,
  ErrorCodes,
  PartOfDay,
  ReservationsResponse,
  VehicleRequest,
  VehicleResponse,
} from '@/assets/composables/types'

const device = getDevice()

const PARKING_PATH = device.cordova
  ? `https://www.pelikan.sk/services/int/public/pelikanec/api/v1/parking`
  : '/api/v1/parking'
const ACCOUNT_PATH = device.cordova
  ? `https://www.pelikan.sk/services/int/public/pelikanec/api/v1/account`
  : '/api/v1/account'
const PUSH_PATH = device.cordova
  ? `https://www.pelikan.sk/services/int/public/pelikanec/api/v1/push`
  : '/api/v1/push'

const frontendVersion = {
  'X-Frontend-Version': __APP_VERSION__,
}

const withPermissionCheck = <T extends any[], R>(fn: (...args: T) => Promise<R>) => {
  return async (...args: T): Promise<R> => {
    const hasPermissions = await checkUserPermissions()
    if (!hasPermissions) {
      const error: any = new Error('Permission denied - user logged out')
      error.response = {
        _data: {
          code: ErrorCodes.permissionDenied,
        },
      }
      throw error
    }
    return await fn(...args)
  }
}

export const activateUser = async () => {
  return await ofetch(`${ACCOUNT_PATH}/activate`, {
    timeout: 12_000,
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
  })
}

export const getDefaultSettings = async (): Promise<DefaultSettings> => {
  const settings = await ofetch<DefaultSettings>(`${PARKING_PATH}/settings`, {
    timeout: 12_000,
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
  })
  if (import.meta.env.DEV) configureDevParkingReturnHours(settings.hoursFrameReturnCard)
  return settings
}

export const getBanStatus = async (): Promise<BanResponse> => {
  return await ofetch(`${PARKING_PATH}/ban`, {
    timeout: 12_000,
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
  })
}

const _getAvailableSlots = async (dateIso: string): Promise<AvailableSlots> => {
  return await ofetch(`${PARKING_PATH}/slots/availability`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
    body: { date: dateIso },
  })
}

export const getAvailableSlots = withPermissionCheck(_getAvailableSlots)

const _getReservations = async (
  specification: string,
  asc: boolean = true,
): Promise<Reservation[]> => {
  const reservations = await ofetch<ReservationsResponse>(`${PARKING_PATH}/reservations`, {
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      'X-Specification': specification,
      ...frontendVersion,
    },
  })

  // Pass selected local fixtures through the same mapper and class as API rows.
  if (import.meta.env.DEV) {
    reservations.rows.push(...getDevParkingReservations(specification))
  }

  reservations.rows.sort((a: { date: string }, b: { date: string }) => {
    const dateA = DateTime.fromISO(a.date)
    const dateB = DateTime.fromISO(b.date)

    if (dateA < dateB) return asc ? -1 : 1
    if (dateA > dateB) return asc ? 1 : -1
    return 0
  })

  return reservations.rows.map((r) => new Reservation(ToReservation(r)))
}

export const getReservations = withPermissionCheck(_getReservations)

export const getCardLoans = withPermissionCheck(
  async (offset: number = 0): Promise<CardLoanList> => {
    const fixtures = import.meta.env.DEV ? getDevCardLoans() : []
    const result = await ofetch<CardLoanList>(`${PARKING_PATH}/card-loans`, {
      timeout: 12_000,
      query: { offset: Math.max(0, offset - fixtures.length), limit: 50 },
      headers: { Authorization: `Bearer ${await getAuthorizationToken()}`, ...frontendVersion },
    })
    if (import.meta.env.DEV && offset === 0) {
      result.rows.unshift(...fixtures)
    }
    result.total += fixtures.length
    return result
  },
)

export const returnCardLoan = withPermissionCheck(async (id: string): Promise<void> => {
  if (import.meta.env.DEV) {
    const fixture = devParkingReservations.find((res) => devCardLoanId(res.id) === id)
    if (fixture) {
      fixture.cardReturned = true
      return
    }
  }
  await ofetch(`${PARKING_PATH}/card-loans/${id}/return`, {
    method: 'POST',
    timeout: 12_000,
    headers: { Authorization: `Bearer ${await getAuthorizationToken()}`, ...frontendVersion },
  })
})

const _returnCard = async (id: number) => {
  const fixture = import.meta.env.DEV
    ? devParkingReservations.find((reservation) => reservation.id === id)
    : undefined
  if (fixture) {
    fixture.cardReturned = true
    return
  }
  return await ofetch(`${PARKING_PATH}/reservations/${id}/return-card`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
  })
}

export const returnCard = withPermissionCheck(_returnCard)

const _deleteReservation = async (id: number, isCardReturned: boolean) => {
  const fixture = import.meta.env.DEV
    ? devParkingReservations.find((reservation) => reservation.id === id)
    : undefined
  if (fixture) {
    // Match the server's fresh settings lookup at the cancellation transition.
    await getDefaultSettings()
    cancelDevParkingReservation(id, isCardReturned)
    return
  }
  return await ofetch(`${PARKING_PATH}/reservations/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      'X-Card-Returned': String(isCardReturned),
      ...frontendVersion,
    },
  })
}

export const deleteReservation = withPermissionCheck(_deleteReservation)

const _reserveSlot = async (dateIso: string, partOfDay: PartOfDay): Promise<unknown> => {
  return await ofetch(`${PARKING_PATH}/reservations`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
    body: {
      date: dateIso,
      partOfDay,
    },
  })
}

export const reserveSlot = withPermissionCheck(_reserveSlot)

const _addVehicle = async (vehicle: VehicleRequest) => {
  return await ofetch(`${PARKING_PATH}/vehicles`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
    body: vehicle,
  })
}

export const addVehicle = withPermissionCheck(_addVehicle)

const _getAllVehicles = async (): Promise<Vehicle[]> => {
  const vehicles = await ofetch<VehicleResponse[]>(`${PARKING_PATH}/vehicles`, {
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
  })

  return vehicles.map((v) => new Vehicle(v))
}

export const getAllVehicles = withPermissionCheck(_getAllVehicles)

const _deleteVehicle = async (id: VehicleResponse['id']) => {
  return await ofetch(`${PARKING_PATH}/vehicles/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
  })
}

export const deleteVehicle = withPermissionCheck(_deleteVehicle)

export const registerDeviceToken = async (token: string, platform: string): Promise<void> => {
  await ofetch(`${PUSH_PATH}/device-tokens`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
    body: { token, platform },
  })
}

export const unregisterDeviceToken = async (token: string): Promise<void> => {
  await ofetch(`${PUSH_PATH}/device-tokens`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${await getAuthorizationToken()}`,
      ...frontendVersion,
    },
    body: { token },
  })
}

export const getSupabaseSession = async () => {
  return await supabase.auth.getSession()
}

export const signInWithOAuth = async (
  options: Parameters<typeof supabase.auth.signInWithOAuth>[0],
) => {
  return await supabase.auth.signInWithOAuth(options)
}

export const signInWithPassword = async (email: string, password: string) => {
  return await supabase.auth.signInWithPassword({ email, password })
}

export const supabaseSignOut = async () => {
  // Sign out locally only — backoffice shares the same auth database, so a global
  // sign-out would also invalidate active backoffice sessions.
  return await supabase.auth.signOut({ scope: 'local' })
}

export const refreshSupabaseSession = async () => {
  return await supabase.auth.refreshSession()
}

export const resetPasswordForEmail = async (email: string, redirectTo: string) => {
  return await supabase.auth.resetPasswordForEmail(email, { redirectTo })
}

export const updateUserPassword = async (password: string) => {
  return await supabase.auth.updateUser({ password })
}
